import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { isDebt, ACCOUNT_TYPE_LIST } from '../constants/ACCOUNT_TYPE'
import AccountPreview from "../components/Account/AccountPreview";
import {
    svc_getAccount,
    svc_updateDebtAccount,
    svc_updateAccount,
    svc_deleteAccount
} from "../services/account.service";
import { IconAngleLeftBold } from "../assets/icons";
import AccountIncludedTooltip from "../components/Account/AccountIncludedTooltip";
import ButtonLoadingWrap from "../components/Buttons/ButtonLoadingWrap";
import { useRCFormContext, RCFormProvider, FFToggle, FFText, FFDate, FFSelect } from '@jlafica/rcform';
import {
    fieldNames,
    initialState,
    getAccountEditFormFieldsModel,
    getCalculatedAvailableBalance,
    getAccountSubmitDataFromFields,
    getDebtAccountSubmitDataFromFields,
    getNormalizedRawAccountData
} from "../models/AccountModel";
import { useResponseMessages } from "../hooks/useResponseMessage";

const Account = ({accountId, cssClassName}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    const scrollAnchrorRef = useRef(null)
    const [account, setAccount] = useState(null)
    let { id } = useParams()
    id = id ?? accountId
    const [debtAccountId, setDebtAccountId] = useState(null)
    const [isEditing, setIsEditing] = useState(false)
    const [formRef, setFormRef] = useState(null);
    const { addMessage } = useResponseMessages();
    const [ isFetching, setIsFetching ] = useState(true)
    const navigate = useNavigate();

    const calculateAvailableBalance = (fields) => getCalculatedAvailableBalance(fields)

    const getAccount = async (skipFormInitialization = false) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_getAccount(accessToken, user.sub, id);
        let accountData = await data[0]?.account
        setIsFetching(false);

        if (error || !accountData) {
            addMessage({
                error: [`No account found matching ID ${id}`, error?.message],
            })
            navigate('/accounts')
            return
        }

        accountData = typeof accountData === 'string'
            ? JSON.parse(accountData)
            : accountData;

        const normalizedAccountData = getNormalizedRawAccountData(accountData)
        const fields = getAccountEditFormFieldsModel(normalizedAccountData)
        
        setAccount(normalizedAccountData)
        setDebtAccountId(accountData.hasOwnProperty('debtAccountInfo') ? accountData.debtAccountInfo.id : null)

        if (!skipFormInitialization) {
            formRef?.updateFieldValues(fields)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        const accessToken = await getAccessTokenSilently();
        let errors = []

        if (isDebt(account.type)) {
            const debtAccountData = getDebtAccountSubmitDataFromFields(formRef?.formState.fields, id, debtAccountId);
            const { error } = await svc_updateDebtAccount(accessToken, user.sub, debtAccountData);

            if (error) {
                errors.push(error.message)
            }
        }

        const accountData = getAccountSubmitDataFromFields(formRef?.formState.fields, id);

        const { error } = await svc_updateAccount(accessToken, user.sub, accountData);

        if (error) {
            errors.push(error.message)
        }

        if (errors.length > 0) {
            addMessage({
                error: errors.join(', '),
            })
        } else {
            addMessage({
                data: 'Account updated successfully'
            })

            getAccount(true)
        }
    }

    const deleteAccount = async (e) => {
        e.preventDefault()
        const accessToken = await getAccessTokenSilently();

        let confirmDelete = window.confirm(
            'Are you sure you want to delete this account?',
        );

        if (account && confirmDelete) {
            const { error } = await svc_deleteAccount(accessToken, user.sub, id);

            if (error) {
                addMessage({
                    error: error.message
                })
            } else {
                addMessage({
                    data: 'Account deleted successfully'
                })

                navigate('/accounts');
            }
        }
    }

    useEffect(() => {
        if (isFetching && !account && formRef) {
            getAccount();
        }
    }, [isFetching, account, formRef]);

    return (
        <div className="content-width-container">
            <Link to="/accounts" className="button button-slim button-blue spc-m-b-1 spc-m-t-1">
                <IconAngleLeftBold fill="#fff" /> Back to My Accounts
            </Link>

            <div ref={scrollAnchrorRef} className={`standardFormWrap ${cssClassName ?? ''}`}>
                {account && (
                    <>
                        <h1 className="text-center spc-m-t-0 spc-m-b-1">Account Details</h1>

                        <AccountPreview account={account} disableLink={true} addBorder={true} />

                        {account.includeBalance === 1 && (
                            <div className="spc-m-t-1">
                                <div>
                                    The available balance for this account is included in your budget funds.
                                    <AccountIncludedTooltip />
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => setIsEditing(!isEditing)}
                            className={`button spc-m-t-1 dis-block-xs ${isEditing ? "button-stroke" : "button-green"}`}
                        >
                            {isEditing ? 'Close Editor' : 'Click to Edit'}
                        </button>
                    </>
                )}

                <RCFormProvider
                    onSubmit={handleSubmit}
                    id="accountEditForm"
                    className={`formStandard ${!formRef?.formState?.isValid ? 'errors' : ''}`}
                    initialFieldState={initialState}
                    setFormRef={setFormRef}
                >
                    <FormContent
                        fieldNames={fieldNames}
                        calculateAvailableBalance={calculateAvailableBalance}
                        onDelete={deleteAccount}
                        onSubmit={handleSubmit}
                        isEditing={isEditing}
                    />
                </RCFormProvider>

            </div>
        </div>
    )
}

const FormContent = ({
    fieldNames,
    calculateAvailableBalance,
    onDelete,
    onSubmit,
    isEditing
}) => {
    const formContext = useRCFormContext();

    return (
        <>
            <FFToggle
                fieldName={fieldNames.includeBalance}
                label={"Include Available Balance in Budget Funds"}
                wrapperModifierClass={"formField"}
                buttonOffValue={"No"}
                buttonOnValue={"Yes"}
                description={formContext?.formState.fields[fieldNames.includeBalance].checked && <p>By selecting "Yes", this account's available balance will be included in the starting balance of your budget forecast.</p>}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                type="number"
                fieldName={fieldNames.availableBalance}
                label={"Available Balance"}
                displayCondition={!isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                additionalProps={{
                    step: .01,
                }}
                formatValue={(value) => parseFloat(value).toFixed(2)}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            {isDebt(formContext?.formState.fields[fieldNames.type].value) && (
                <div className="formField">
                    <label>Calculated Available Balance (Limit - Balance)</label>
                    <div className="mock-disabled-input">
                        {parseFloat(calculateAvailableBalance(formContext?.formState.fields)).toFixed(2)}
                    </div>
                </div>
            )}

            <FFText
                type="number"
                fieldName={fieldNames.balance}
                label={"Balance*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                fieldName={fieldNames.name}
                label={"Name*"}
                wrapperModifierClass={"formField"}
                required={true}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                fieldName={fieldNames.institution}
                label={"Institution"}
                wrapperModifierClass={"formField"}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                fieldName={fieldNames.accountNumber}
                label={"Account Number"}
                wrapperModifierClass={"formField"}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFSelect
                fieldName={fieldNames.status}
                label={"Status"}
                wrapperModifierClass={"formField"}
                options={[
                    { value: "Open", label: "Open" },
                    { value: "Closed", label: "Closed" }
                ]}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFSelect
                fieldName={fieldNames.type}
                label={"Type"}
                wrapperModifierClass={"formField"}
                options={ACCOUNT_TYPE_LIST.map((type) => ({
                    value: type,
                    label: type
                }))}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            {isDebt(formContext?.formState.fields[fieldNames.type].value) && (
                <h3 className="spc-m-t-1">
                    Debt Info
                </h3>
            )}

            <FFText
                type="number"
                fieldName={fieldNames.lineLimit}
                label={"Limit*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                type="number"
                fieldName={fieldNames.minimumPaymentPercentage}
                label={`Minimum Payment Percentage* ${Boolean(formContext?.formState.fields[fieldNames.minimumPaymentPercentage].value) ? '[' + parseFloat(formContext?.formState.fields[fieldNames.minimumPaymentPercentage].value * 100).toFixed(2) + "%]" : ''}`}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                additionalProps={{
                    step: ".01",
                    min: "0",
                    max: "100"
                }}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                type="number"
                fieldName={fieldNames.minimumPaymentLowBalance}
                label={"Minimum Payment for Low Balance*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                type="number"
                fieldName={fieldNames.fixedPayment}
                label={"Fixed Payment"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFDate
                fieldName={fieldNames.introRateEndDate}
                label={"Intro Rate End Date"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                type="number"
                fieldName={fieldNames.apr}
                label={`APR* ${Boolean(formContext?.formState.fields[fieldNames.apr].value) ? '[' + parseFloat(formContext?.formState.fields[fieldNames.apr].value * 100).toFixed(2) + "%]" : ''}`}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                additionalProps={{
                    step: ".01",
                    min: "0",
                    max: "100"
                }}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                type="number"
                fieldName={fieldNames.introApr}
                label={`Intro APR ${Boolean(formContext?.formState.fields[fieldNames.introApr].value) ? '[' + parseFloat(formContext?.formState.fields[fieldNames.introApr].value * 100).toFixed(2) + "%]" : ''}`}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                additionalProps={{
                    step: ".01",
                    min: "0",
                    max: "100"
                }}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <FFText
                type="number"
                fieldName={fieldNames.dueDate}
                label={"Due Date*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                additionalProps={{
                    min: "1",
                    max: "28",
                    step: "1"
                }}
                readOnly={!isEditing}
                disabled={!isEditing}
            />

            <div className="formField button-row">
                <ButtonLoadingWrap button={
                    <button onClick={(e) => onSubmit(e)} type="submit" className="button button-green">
                        Apply Updates
                    </button>
                } />

                <button
                    className="button button-red"
                    onClick={onDelete}
                >
                    Delete Account
                </button>
            </div>
        </>
    )
}

export default Account

